import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { BookOpen, CalendarCheck2, ClipboardCheck, Gavel, Landmark, Megaphone, MessagesSquare, Package, Scale, Users, Wrench } from 'lucide-react'
import type { ComponentType } from 'react'
import { get } from '../lib/api.ts'
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format.ts'
import { useCondo } from '../lib/hooks.ts'
import { Badge, Empty, PageHeader, Sheet, Spinner } from '../components/ui.tsx'
import { OUTCOME_LABEL } from './opinions.tsx'
import { LOCALE, t } from '../lib/i18n.ts'

/** Painel do síndico (ADR 0028): one read, the numbers of the day and the compliance score. */
type Outcome = 'aprovado' | 'com_ressalvas' | 'reprovado'
interface Dashboard {
  today: string
  compliance: { score: number | null; total: number; scheduled: number; inDate: number; unscheduled: number; overdue: { title: string; nextDue: string; daysLate: number }[]; dueSoon: { title: string; nextDue: string; daysLeft: number }[] }
  attention: { openTickets: number; urgentTickets: number; pendingReservations: number; pendingDeliveries: number; pendingDecisions: number; unansweredChats: number; unseenLog: number; nextAssembly: { id: string; title: string; at: string; status: string } | null }
  finance: {
    month: string
    receitas: number
    despesas: number
    budget: { year: number; monthsElapsed: number; totals: Record<'receita' | 'despesa', { budget: number; actual: number; expected: number; projected: number; pct: number | null }>; balance: { budget: number; actual: number; projected: number } } | null
    lastStatement: { id: string; period: string; totals: Record<string, number | null>; published: boolean; parecer: { count: number; councilSize: number; complete: boolean; outcome: Outcome | null } } | null
  }
  notices: { last30: number; readRate: number | null }
  people: { members: number; units: number; unitsLinked: number }
}

const monthLabel = (m: string) => new Intl.DateTimeFormat(LOCALE, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${m.slice(0, 7)}-15T12:00:00Z`))
export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`
export const scoreTone = (s: number | null): 'neutral' | 'carimbo' | 'alerta' | 'margem' => (s === null ? 'neutral' : s >= 90 ? 'carimbo' : s >= 70 ? 'alerta' : 'margem')
export const scoreLabel = (s: number | null) => (s === null ? t('sem prazos com data') : s >= 90 ? t('em dia') : s >= 70 ? t('atenção') : t('em atraso'))

export type Attention = Dashboard['attention']
export interface Counter { to: string; icon: ComponentType<{ className?: string }>; n: number; text: string; extra?: string | null }

/** The painel's counters, also shown per condo in Meus condomínios (ADR 0043). */
export function attentionCounters(a: Attention): Counter[] {
  return [
    { to: '/c/$slug/chamados', icon: Wrench, n: a.openTickets, text: plural(a.openTickets, t('chamado aberto'), t('chamados abertos')), extra: a.urgentTickets ? plural(a.urgentTickets, t('urgente'), t('urgentes')) : null },
    { to: '/c/$slug/reservas', icon: CalendarCheck2, n: a.pendingReservations, text: plural(a.pendingReservations, t('reserva aguardando'), t('reservas aguardando')) },
    { to: '/c/$slug/portaria', icon: Package, n: a.pendingDeliveries, text: plural(a.pendingDeliveries, t('encomenda na portaria'), t('encomendas na portaria')) },
    { to: '/c/$slug/advertencias', icon: Scale, n: a.pendingDecisions, text: plural(a.pendingDecisions, t('notificação aguardando decisão'), t('notificações aguardando decisão')) },
    { to: '/c/$slug/conversas', icon: MessagesSquare, n: a.unansweredChats, text: plural(a.unansweredChats, t('conversa sem resposta'), t('conversas sem resposta')) },
    { to: '/c/$slug/portaria', icon: BookOpen, n: a.unseenLog, text: plural(a.unseenLog, t('ocorrência da portaria sem visto'), t('ocorrências da portaria sem visto')) },
  ]
}

export function Painel() {
  const { slug } = useParams({ strict: false }) as { slug: string }
  const { condoId, can } = useCondo(slug)
  const allowed = can('finance.view')
  const q = useQuery({ queryKey: ['painel', condoId], queryFn: () => get<Dashboard>(`/api/condos/${condoId}/painel`), enabled: Boolean(condoId && allowed), staleTime: 30_000 })
  if (!allowed) return <Empty title={t('Painel')}>{t('Só quem vê as finanças do condomínio acessa o painel.')}</Empty>
  if (!q.data) return <Spinner />
  const d = q.data
  const c = d.compliance
  const a = d.attention
  const counters = attentionCounters(a)
  const st = d.finance.lastStatement
  const b = d.finance.budget
  return (
    <div>
      <PageHeader title={t('Painel')} />
      <p className="mb-4 max-w-prose text-tinta-2">{t('Os números de hoje,')}{' '}{fmtDate(d.today)}{t(', a partir dos registros do aplicativo. O resumo de segunda-feira no WhatsApp traz os mesmos itens.')}</p>

      <Sheet className="mb-4 p-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div>
            <p className="text-sm text-tinta-2">{t('Conformidade dos prazos')}</p>
            <p className="text-5xl font-bold leading-none tracking-tight" data-testid="score">{c.score === null ? '—' : `${c.score}%`}</p>
          </div>
          <div className="flex flex-col gap-1">
            <Badge tone={scoreTone(c.score)}>{scoreLabel(c.score)}</Badge>
            <span className="text-sm text-tinta-2">{c.total === 0 ? t('Cadastre os prazos do prédio pelo catálogo de manutenção.') : c.scheduled === 0 ? (c.unscheduled === 1 ? t('1 prazo cadastrado sem data: registre em Prazos quando cada item foi feito pela última vez.') : t('{n} prazos cadastrados sem data: registre em Prazos quando cada item foi feito pela última vez.', { n: c.unscheduled })) : `${c.scheduled === 1 ? t('{done} de 1 prazo com data em dia', { done: c.inDate }) : t('{done} de {n} prazos com data em dia', { done: c.inDate, n: c.scheduled })}${c.unscheduled ? (c.unscheduled === 1 ? t('; 1 prazo sem data fora do índice') : t('; {n} prazos sem data fora do índice', { n: c.unscheduled })) : ''}`}</span>
          </div>
        </div>
        {c.overdue.length > 0 && (
          <ul className="mt-3 flex flex-col gap-1 text-sm">
            {c.overdue.map((o, i) => (
              <li key={`${o.title}:${i}`} className="flex flex-wrap items-baseline gap-x-2"><ClipboardCheck className="inline size-4 self-center text-margem" /><span className="font-semibold">{o.title}</span><span className="text-tinta-2">{t('venceu em')}{' '}{fmtDate(o.nextDue)}, {plural(o.daysLate, t('dia'), t('dias'))}{' '}{t('atrás')}</span></li>
            ))}
          </ul>
        )}
        {c.dueSoon.length > 0 && <p className="mt-2 text-sm text-tinta-2">{t('Próximos 30 dias:')}{' '}{c.dueSoon.map((o) => `${o.title} (${fmtDate(o.nextDue)})`).join(', ')}.</p>}
        <Link to="/c/$slug/prazos" params={{ slug }} className="mt-3 inline-block text-sm font-semibold text-caneta underline">{t('Ver prazos')}</Link>
      </Sheet>

      <div className="mb-4 grid grid-cols-2 gap-3">
        {/* two counters link to the gatehouse (deliveries, log), so the key is the position, not the route */}
        {counters.map((k, i) => (
          <Link key={i} to={k.to} params={{ slug }} className="rounded-ficha border border-pauta bg-folha p-3 hover:bg-papel">
            <k.icon className="size-5 text-caneta" />
            <p className="mt-1 text-2xl font-bold leading-none">{k.n}</p>
            <p className="text-sm text-tinta-2">{k.text.replace(/^\d+ /, '')}</p>
            {k.extra && <p className="text-sm font-semibold text-margem">{k.extra}</p>}
          </Link>
        ))}
        <Link to={a.nextAssembly ? '/c/$slug/assembleias/$aid' : '/c/$slug/assembleias'} params={a.nextAssembly ? { slug, aid: a.nextAssembly.id } : { slug }} className="rounded-ficha border border-pauta bg-folha p-3 hover:bg-papel">
          <Gavel className="size-5 text-caneta" />
          {a.nextAssembly ? (
            <>
              <p className="mt-1 font-bold leading-tight">{a.nextAssembly.status === 'aberta' ? t('Votação aberta') : t('Assembleia convocada')}</p>
              <p className="text-sm text-tinta-2">{a.nextAssembly.title}, {fmtDateTime(a.nextAssembly.at)}</p>
            </>
          ) : (
            <>
              <p className="mt-1 text-2xl font-bold leading-none">0</p>
              <p className="text-sm text-tinta-2">{t('assembleias convocadas')}</p>
            </>
          )}
        </Link>
      </div>

      <Sheet className="mb-4 p-4">
        <h2 className="flex items-center gap-2 font-bold"><Landmark className="size-5 text-caneta" />{' '}{t('Finanças em')}{' '}{monthLabel(d.finance.month)}</h2>
        <dl className="mt-2 grid grid-cols-2 gap-2 text-center">
          <div className="rounded-ficha bg-papel p-2"><dt className="text-sm text-tinta-2">{t('Receitas lançadas')}</dt><dd className="font-bold">{fmtMoney(d.finance.receitas)}</dd></div>
          <div className="rounded-ficha bg-papel p-2"><dt className="text-sm text-tinta-2">{t('Despesas lançadas')}</dt><dd className="font-bold">{fmtMoney(d.finance.despesas)}</dd></div>
        </dl>
        {b ? (
          <p className="mt-2 text-sm text-tinta-2">{t('Previsão')}{' '}{b.year}{t(': despesas em')}{' '}{b.totals.despesa.pct === null ? '—' : `${b.totals.despesa.pct}%`}{' '}{t('do previsto para o ano,')}{' '}{fmtMoney(b.totals.despesa.actual)}{' '}{t('de')}{' '}{fmtMoney(b.totals.despesa.budget)} ({plural(b.monthsElapsed, t('mês'), 'meses')}{' '}{t('decorrido')}{b.monthsElapsed === 1 ? '' : 's'}{t('); saldo previsto')}{' '}{fmtMoney(b.balance.budget)}{t(', realizado')}{' '}{fmtMoney(b.balance.actual)}.</p>
        ) : (
          <p className="mt-2 text-sm text-tinta-2">{t('Sem previsão orçamentária para este ano.')}</p>
        )}
        {st ? (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span>{t('Último balancete:')}{' '}<span className="font-semibold">{monthLabel(st.period)}</span>{st.totals.saldo != null ? `, saldo ${fmtMoney(st.totals.saldo)}` : ''}{st.published ? '' : t(' (não publicado)')}</span>
            {st.parecer.councilSize > 0 && (st.parecer.outcome ? <Badge tone={st.parecer.outcome === 'aprovado' ? 'carimbo' : st.parecer.outcome === 'com_ressalvas' ? 'alerta' : 'margem'}>{OUTCOME_LABEL[st.parecer.outcome]} {st.parecer.count}/{st.parecer.councilSize}</Badge> : <Badge tone="neutral">{t('parecer pendente')}{' '}{st.parecer.count}/{st.parecer.councilSize}</Badge>)}
          </p>
        ) : (
          <p className="mt-2 text-sm text-tinta-2">{t('Nenhum balancete registrado.')}</p>
        )}
        <Link to="/c/$slug/financas" params={{ slug }} className="mt-3 inline-block text-sm font-semibold text-caneta underline">{t('Ver finanças')}</Link>
      </Sheet>

      <div className="grid gap-3 sm:grid-cols-2">
        <Sheet className="p-4">
          <h2 className="flex items-center gap-2 font-bold"><Megaphone className="size-5 text-caneta" />{' '}{t('Avisos')}</h2>
          <p className="mt-1 text-sm text-tinta-2">{d.notices.last30 === 1 ? t('1 aviso nos últimos 30 dias') : t('{n} avisos nos últimos 30 dias', { n: d.notices.last30 })}{d.notices.readRate === null ? '' : t(', taxa de leitura {pct}%', { pct: d.notices.readRate })}.</p>
          <Link to="/c/$slug/avisos" params={{ slug }} className="mt-2 inline-block text-sm font-semibold text-caneta underline">{t('Ver avisos')}</Link>
        </Sheet>
        <Sheet className="p-4">
          <h2 className="flex items-center gap-2 font-bold"><Users className="size-5 text-caneta" />{' '}{t('Pessoas')}</h2>
          <p className="mt-1 text-sm text-tinta-2">{plural(d.people.members, t('membro'), t('membros'))}; {d.people.unitsLinked}{' '}{t('de')}{' '}{plural(d.people.units, t('unidade'), t('unidades'))}{' '}{t('com gente vinculada.')}</p>
          <Link to="/c/$slug/unidades" params={{ slug }} className="mt-2 inline-block text-sm font-semibold text-caneta underline">{t('Ver unidades')}</Link>
        </Sheet>
      </div>
    </div>
  )
}
